import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { AUTH_PATHS } from '../api/auth.api';
import { AuthStore } from '../state/auth.store';

export const REFRESH_RETRY = new HttpContextToken<boolean>(() => false);

const AUTH_ENDPOINTS = [AUTH_PATHS.login, AUTH_PATHS.refresh, AUTH_PATHS.logout];

let refresh$: Observable<string | null> | null = null;

export function resetRefreshState(): void {
  refresh$ = null;
}

function pathOf(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split('?')[0];
  }
}

export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      const path = pathOf(req.url);
      if (AUTH_ENDPOINTS.some((p) => path.endsWith(p))) {
        return throwError(() => error);
      }

      if (req.context.get(REFRESH_RETRY)) {
        authStore.sessionExpiredLogout();
        return throwError(() => error);
      }

      if (!refresh$) {
        refresh$ = authStore.refreshAccessToken$().pipe(
          catchError(() => of(null)),
          finalize(() => {
            refresh$ = null;
          }),
          shareReplay(1),
        );
      }

      return refresh$.pipe(
        switchMap((token) => {
          if (!token) {
            authStore.sessionExpiredLogout();
            return throwError(() => error);
          }
          return next(
            req.clone({
              context: req.context.set(REFRESH_RETRY, true),
              setHeaders: { Authorization: `Bearer ${token}` },
            }),
          ).pipe(
            catchError((retryError: unknown) => {
              if (retryError instanceof HttpErrorResponse && retryError.status === 401) {
                authStore.sessionExpiredLogout();
              }
              return throwError(() => retryError);
            }),
          );
        }),
      );
    }),
  );
};
